import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, imgSrc } from "../../lib/api";
import { AdminShell } from "../../components/AdminShell";

const INPUT_CLASS =
  "w-full border border-white/10 bg-[#0d0d0d] px-4 py-3 text-sm font-light text-[#F5F5F5] placeholder:text-[#5a5a5a] transition-colors duration-300 focus:border-gold focus:outline-none rounded-none";

const KATEGORI = [
  "Hukum Pidana", "Hukum Perdata", "Hukum Keluarga",
  "Pertanahan & Agraria", "Ketenagakerjaan", "Hukum Perusahaan", "Bantuan Hukum",
];

const BIDANG = [
  ["ringkasan_kasus", "Ringkasan Kasus"],
  ["kronologi", "Kronologi Singkat"],
  ["strategi", "Langkah Hukum & Strategi"],
  ["hasil", "Hasil Akhir & Dampak"],
];

const EMPTY = {
  judul: "", kategori: KATEGORI[0],
  ringkasan_kasus: "", kronologi: "", strategi: "", hasil: "",
  gambar: "", file: null, preview: null,
};

export default function AdminKasus() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/kasus").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditId(null); setForm({ ...EMPTY }); };
  const openEdit = (k) => {
    setEditId(k.id);
    setForm({
      judul: k.judul, kategori: k.kategori,
      ringkasan_kasus: k.ringkasan_kasus, kronologi: k.kronologi,
      strategi: k.strategi, hasil: k.hasil,
      gambar: k.gambar || "", file: null, preview: null,
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let gambar = form.gambar || null;
      if (form.file) {
        const fd = new FormData();
        fd.append("file", form.file);
        const up = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        gambar = up.data.url;
      }
      const payload = {
        judul: form.judul, kategori: form.kategori,
        ringkasan_kasus: form.ringkasan_kasus, kronologi: form.kronologi,
        strategi: form.strategi, hasil: form.hasil, gambar,
      };
      if (editId) {
        await api.put(`/kasus/${editId}`, payload);
        toast.success("Kasus diperbarui.");
      } else {
        await api.post("/kasus", payload);
        toast.success("Kasus baru diterbitkan.");
      }
      setForm(null);
      load();
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("ylbh_admin_token");
        navigate("/admin/login");
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || "Gagal menyimpan kasus.");
      }
    } finally {
      setSaving(false);
    }
  };

  const hapus = async (k) => {
    if (!window.confirm(`Hapus kasus "${k.judul}"? Tindakan ini tidak bisa dibatalkan.`)) return;
    try {
      await api.delete(`/kasus/${k.id}`);
      toast.success("Kasus dihapus.");
      load();
    } catch {
      toast.error("Gagal menghapus kasus.");
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <AdminShell title="Kelola Kasus">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-serif text-4xl text-[#F5F5F5]">Direktori Kasus</h1>
        <button
          onClick={openCreate}
          data-testid="kasus-baru-button"
          className="flex items-center gap-2 bg-gold px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-ink transition-all hover:bg-gold-light"
        >
          <Plus size={15} /> Tambah Kasus
        </button>
      </div>

      {form && (
        <form
          data-testid="kasus-form"
          onSubmit={submit}
          className="mt-8 space-y-5 border border-gold/30 bg-[#0A0A0A] p-6 lg:p-8"
        >
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-2xl text-[#F5F5F5]">
              {editId ? "Ubah Kasus" : "Kasus Baru"}
            </h2>
            <button type="button" onClick={() => setForm(null)} aria-label="Tutup formulir" data-testid="kasus-form-close">
              <X size={20} className="text-[#737373] hover:text-white" />
            </button>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]">Judul Kasus *</label>
              <input data-testid="kasus-judul-input" required minLength={5} value={form.judul} onChange={set("judul")} className={INPUT_CLASS} placeholder="Judul studi kasus (identitas klien disamarkan)" />
            </div>
            <div>
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]">Kategori *</label>
              <select data-testid="kasus-kategori-select" value={form.kategori} onChange={set("kategori")} className={`${INPUT_CLASS} appearance-none`}>
                {KATEGORI.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            {BIDANG.map(([field, label]) => (
              <div key={field} className="sm:col-span-2">
                <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]">
                  {label} * — pisahkan paragraf dengan baris kosong
                </label>
                <textarea
                  data-testid={`kasus-${field.replace(/_/g, "-")}-textarea`}
                  required
                  minLength={20}
                  rows={4}
                  value={form[field]}
                  onChange={set(field)}
                  className={`${INPUT_CLASS} resize-y`}
                />
              </div>
            ))}
            <div className="sm:col-span-2">
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]">
                Gambar Sampul — unggah dari komputer (opsional, maks 5 MB)
              </label>
              <div className="flex flex-wrap items-center gap-5">
                <label
                  data-testid="kasus-upload-label"
                  className="flex cursor-pointer items-center gap-2 border border-dashed border-gold/50 px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-ink"
                >
                  <ImagePlus size={15} /> Pilih Gambar
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    data-testid="kasus-file-input"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) setForm((prev) => ({ ...prev, file: f, preview: URL.createObjectURL(f) }));
                    }}
                  />
                </label>
                {(form.preview || form.gambar) && (
                  <img
                    src={form.preview || imgSrc(form.gambar)}
                    alt="Pratinjau gambar kasus"
                    data-testid="kasus-preview-image"
                    className="h-20 w-32 border border-white/10 object-cover"
                  />
                )}
              </div>
            </div>
          </div>
          <button
            type="submit"
            data-testid="kasus-simpan-button"
            disabled={saving}
            className="bg-gold px-8 py-3.5 text-[12px] font-semibold uppercase tracking-[0.2em] text-ink transition-all hover:bg-gold-light disabled:opacity-60"
          >
            {saving ? "Menyimpan…" : editId ? "Simpan Perubahan" : "Terbitkan"}
          </button>
        </form>
      )}

      <div className="mt-10 space-y-3">
        {items === null ? (
          <p className="py-16 text-center font-serif text-xl italic text-[#737373]">Memuat…</p>
        ) : (
          items.map((k) => (
            <div
              key={k.id}
              data-testid={`kasus-row-${k.slug}`}
              className="flex flex-wrap items-center gap-x-5 gap-y-2 border border-white/10 bg-[#0d0d0d] px-5 py-4"
            >
              <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">{k.kategori}</span>
              <span className="font-serif text-lg text-[#F5F5F5]">{k.judul}</span>
              <span className="text-xs text-[#737373]">{k.tanggal}</span>
              <span className="ml-auto flex gap-2">
                <button
                  onClick={() => openEdit(k)}
                  data-testid={`kasus-edit-${k.slug}`}
                  className="flex items-center gap-1.5 border border-white/15 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors hover:border-gold hover:text-gold"
                >
                  <Pencil size={12} /> Ubah
                </button>
                <button
                  onClick={() => hapus(k)}
                  data-testid={`kasus-hapus-${k.slug}`}
                  className="flex items-center gap-1.5 border border-red-500/30 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.2em] text-red-300/80 transition-colors hover:border-red-400 hover:text-red-300"
                >
                  <Trash2 size={12} /> Hapus
                </button>
              </span>
            </div>
          ))
        )}
      </div>
    </AdminShell>
  );
}
